import React, { useState, useCallback } from 'react';
import {
  Box,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import Appointment from './components/Appointment';
import { useGetAppointments } from '../../hooks/data/appointments';
import Loading from '../../components/Loading';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    periodTitle: {
      color: '#999',
      fontWeight: 'bold',
      marginTop: theme.spacing(2),
    },
    divider: {
      background: '#777',
    },
  }),
);

const Home: React.FC = () => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState<MaterialUiPickersDate>(
    new Date(),
  );

  const morningAppointments = useGetAppointments({
    date: moment(selectedDate).format('DD-MM-yyyy'),
    period: 'morning',
  });

  const afternoonAppointments = useGetAppointments({
    date: moment(selectedDate).format('DD-MM-yyyy'),
    period: 'afternoon',
  });

  const handleDateChange = useCallback((date: MaterialUiPickersDate) => {
    setSelectedDate(date);
  }, []);

  const getMorningMainContent = useCallback(() => {
    if (morningAppointments.isLoading) {
      return <Loading />;
    }

    if (morningAppointments.isError) {
      return (
        <Typography>
          Erro ao buscar os agendamentos do período da manhã
        </Typography>
      );
    }

    if (
      morningAppointments.isSuccess &&
      morningAppointments.data.length === 0
    ) {
      return (
        <Typography>
          Voce não possui agendamentos no período da manhã
        </Typography>
      );
    }

    return (
      morningAppointments.data &&
      morningAppointments?.data.map(appointment => (
        <Appointment appointment={appointment} />
      ))
    );
  }, [
    morningAppointments.data,
    morningAppointments.isError,
    morningAppointments.isLoading,
    morningAppointments.isSuccess,
  ]);

  const getAfternoonMainContent = useCallback(() => {
    if (afternoonAppointments.isLoading) {
      return <Loading />;
    }

    if (afternoonAppointments.isError) {
      return (
        <Typography>
          Erro ao buscar os agendamentos do período da tarde
        </Typography>
      );
    }

    if (
      afternoonAppointments.isSuccess &&
      afternoonAppointments.data.length === 0
    ) {
      return (
        <Typography>
          Voce não possui agendamentos no período da tarde
        </Typography>
      );
    }

    return (
      afternoonAppointments.data &&
      afternoonAppointments?.data.map(appointment => (
        <Appointment appointment={appointment} />
      ))
    );
  }, [
    afternoonAppointments.data,
    afternoonAppointments.isError,
    afternoonAppointments.isLoading,
    afternoonAppointments.isSuccess,
  ]);

  return (
    <>
      <Typography variant="h4">Agendamentos</Typography>
      <Typography variant="body2">
        Abaixo estão todos os agendamentos que você recebeu no dia selecionado
        abaixo. Para ver a agenda de outro dia, altere a data no filtro abaixo
      </Typography>
      <Box marginTop={2} display="flex" justifyContent="flex-end" width="100%">
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            autoOk
            label="Filtro"
            inputVariant="outlined"
            value={selectedDate}
            onChange={handleDateChange}
            format="dd/MM/yyyy"
          />
        </MuiPickersUtilsProvider>
      </Box>
      <Typography className={classes.periodTitle}>Manhã</Typography>
      <Divider className={classes.divider} />
      {getMorningMainContent()}
      <Typography className={classes.periodTitle}>Tarde</Typography>
      <Divider className={classes.divider} />
      {getAfternoonMainContent()}
    </>
  );
};

export { Home };
